import { api } from '@alpha/api-client';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { useSession } from '../../hooks/useSession';
import { formatDateTime, formatLds, statusTone } from '../../lib/format';
import { shortAddress } from '../../lib/wallet';
import { Alert, Card, Chip, Spinner } from '../../ui';
import { PROFILE_TABS } from './Profile';

/**
 * "My Wallet" — the member-facing half of the client requirement "SHOW ALL
 * NECESSARY REGISTRATION DAPP REQUESTS IN ADMIN AS WELL AS USER PANEL". Shows
 * the wallet address this account is bound to and the on-chain registration
 * request that created (or is still pending for) it.
 */
export function WalletStatus() {
  const { user } = useSession();

  const { data: request, isLoading } = useQuery({
    queryKey: ['dapp-request', 'mine'],
    queryFn: () => api.walletAuth.myRequest().then((r) => r.data),
    enabled: !!user,
  });

  return (
    <>
      <PageHeader title="My Wallet" tabs={PROFILE_TABS} />

      <Card>
        <h3 className="card-title">Connected Wallet</h3>
        {user?.wallet_address ? (
          <p className="deposit-address">{user.wallet_address}</p>
        ) : (
          <Alert tone="warning">No wallet address is on file for this account.</Alert>
        )}
      </Card>

      <Card>
        <h3 className="card-title">DApp Registration Request</h3>

        {isLoading ? <Spinner /> : null}

        {!isLoading && !request ? (
          <p className="text-muted small">
            No registration request is on record for this wallet (this account may predate the wallet-registration
            flow).
          </p>
        ) : null}

        {request ? (
          <>
            <p style={{ marginBottom: 8 }}>
              Status: <Chip tone={statusTone(request.status)}>{request.status}</Chip>
            </p>
            <p className="text-muted small">Wallet: {shortAddress(request.wallet_address)}</p>
            <p className="text-muted small">Transaction: {request.tx_hash ? shortAddress(request.tx_hash) : '—'}</p>
            <p className="text-muted small">Amount: {request.amount_lds ? formatLds(request.amount_lds) : '—'}</p>
            <p className="text-muted small">Submitted: {formatDateTime(request.created_at)}</p>
            {request.verified_at ? (
              <p className="text-muted small">Verified: {formatDateTime(request.verified_at)}</p>
            ) : null}
            {request.status === 'rejected' && request.reject_reason ? (
              <Alert tone="danger">{request.reject_reason}</Alert>
            ) : null}
            {request.status === 'pending' ? (
              <Alert tone="warning">
                Awaiting on-chain confirmation. If this has been pending for a long time, contact support with your
                transaction hash.
              </Alert>
            ) : null}
          </>
        ) : null}
      </Card>
    </>
  );
}
